import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles, type Theme } from "@material-ui/core/styles";
import { activeDialogPropsSelector } from "src/store/adminHtml/selector";
import { setActiveDialog } from "src/store/adminHtml/actions";
import type { EditSaleProps } from "src/interfaces/adminHtmlState.interface";
import Edit, {
  type DialogState,
} from "src/pages/TransactionDetails/Sales/Edit";
import type { Sale } from "src/interfaces/sale.interface";
import firestore from "src/apiService/firestore";
import { getUserId } from "src/config/storage";
import { applySaleItemUiFields, getSaleById } from "src/store/sale/selector";
import { getVendors } from "src/store/vendor/selector";
import { bankAccountsSelector } from "src/store/bankAccount/selector/core";
import { asDate } from "src/utils/date";

const db = firestore();

const FetchingSpinnerDelayMS = 200;

export type FetchingSaleDialogState = { type: "fetching-sale"; id: string };

export type EditSaleDialogState = DialogState | FetchingSaleDialogState;

export function isFetchingSaleState(
  state: EditSaleDialogState | undefined
): state is FetchingSaleDialogState {
  return state !== undefined && state.type === "fetching-sale";
}

export function isDialogStateForEdit(
  state: EditSaleDialogState | undefined
): state is DialogState {
  return !isFetchingSaleState(state);
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "contents",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: theme.palette.common.white,
  },
}));

function EditSaleDialog({ open }: { open: boolean }) {
  const classes = useStyles();
  const [_value, setValue] = useState<EditSaleDialogState>();
  const dispatch = useDispatch();
  const store = useStore();
  const updatedRef = useRef(false);

  const activeDialogProps = useSelector(
    activeDialogPropsSelector
  ) as EditSaleProps;
  const id = open ? activeDialogProps?.id : undefined;

  const editValue: DialogState = isDialogStateForEdit(_value)
    ? _value
    : undefined;

  const showFetchingBackdrop = open && isFetchingSaleState(_value);

  useEffect(() => {
    let cancelled = false;
    updatedRef.current = false;
    let spinnerTimer: number | undefined;

    if (!open || !id) {
      setValue(undefined);
      return;
    }

    const state = store.getState();
    if (getSaleById(state, id)) {
      setValue({ type: "edit", id });
      return;
    }

    spinnerTimer = window.setTimeout(() => {
      if (cancelled) return;
      setValue((prev) => {
        if (prev?.type === "edit" && prev.id === id) return prev;
        if (prev && prev.type !== "fetching-sale" && prev.type !== "edit") {
          return prev;
        }
        return { type: "fetching-sale", id };
      });
    }, FetchingSpinnerDelayMS);

    (async () => {
      try {
        const [uid, snapshot] = await Promise.all([
          getUserId(),
          db.collection("Sales").doc(id).get(),
        ]);
        if (cancelled) return;
        if (!snapshot.exists) {
          setValue(undefined);
          return;
        }
        const data = snapshot.data() as Record<string, unknown>;
        if (!data || data.user !== uid) {
          setValue(undefined);
          return;
        }
        const stateNow = store.getState();
        const vendors = getVendors(stateNow);
        const bankAccounts = bankAccountsSelector(stateNow);
        const withDates = {
          ...data,
          purchase_date: asDate(data.purchase_date),
          list_date: asDate(data.list_date),
          sale_date: asDate(data.sale_date),
          return_date: asDate(data.return_date),
          import_date: asDate(data.import_date),
          inventory_import_date: asDate(data.inventory_import_date),
        } as Sale;
        const enriched = applySaleItemUiFields(
          withDates,
          vendors,
          bankAccounts
        );
        if (cancelled) return;
        if (getSaleById(store.getState(), id)) {
          setValue((prev) => {
            if (prev && prev.type !== "edit" && prev.type !== "fetching-sale") {
              return prev;
            }
            return { type: "edit", id };
          });
          return;
        }
        setValue((prev) => {
          if (prev && prev.type !== "edit" && prev.type !== "fetching-sale") {
            return prev;
          }
          return { type: "edit", id, sale: enriched };
        });
      } finally {
        if (spinnerTimer !== undefined) {
          clearTimeout(spinnerTimer);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (spinnerTimer !== undefined) {
        clearTimeout(spinnerTimer);
      }
    };
  }, [open, id, store]);

  return (
    <div className={classes.root}>
      <Backdrop className={classes.backdrop} open={showFetchingBackdrop}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Edit
        value={editValue}
        onChanged={() => {
          updatedRef.current = true;
        }}
        onChange={(dialog) => {
          if (dialog) {
            setValue(dialog);
            return;
          }
          dispatch(
            setActiveDialog("", {
              requestId: activeDialogProps?.requestId,
              updated: updatedRef.current,
            })
          );
          updatedRef.current = false;
          setValue(undefined);
        }}
      />
    </div>
  );
}

export default EditSaleDialog;
